// The slash menu shared by every view (Lexical SlashMenuPlugin, the Skia overlay): its items, the typed trigger that
// opens it, fuzzy filtering, the blocks each item inserts, and where a choice lands (`applySlashItem`).
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { isInline } from '@w/content-blocks/src/translation-units'
import { ulid } from '@w/utils'
import { duplicateCaretBlock } from './duplicate-block'
import { formatBlock } from './format-block'
import type { EditorIntent } from './intents'
import { deleteRange, spliceBlocks, takeInlineTail } from './ops'
import { after, insertAt } from './placement'
import { inInlineSlot, isInlineContent } from './levels'
import type { CorePoint, CoreState } from './state'
import { children, findBlock, locate, plain, pointAt, rows, type Row } from './tree-read'
import { blockClass } from './block-selection'

export type SlashMenuSection = 'Turn into' | 'Basic' | 'Study' | 'Layout' | 'Media' | 'More'
type BlockKind = Extract<EditorIntent, { type: 'formatBlock' }>['block']

/** What choosing an item does: a block conversion, a block template (built for the replaced block's id), a file picker or the link editor. */
export type SlashAction =
  | { kind: 'format'; block: BlockKind }
  | { kind: 'insert'; build: (id: string) => Block }
  | { kind: 'files'; accept: 'image' | 'file' | 'video' }
  | { kind: 'link' }
  | { kind: 'duplicate' }
  /** Notion `/turn…`: the caret's block becomes this kind in place, keeping its text and caret. */
  | { kind: 'turn'; block: BlockKind }

export interface SlashItem {
  title: string
  keywords: readonly string[]
  section: SlashMenuSection
  action: SlashAction
}

export const slashSections: readonly SlashMenuSection[] = ['Turn into', 'Basic', 'Study', 'Layout', 'Media', 'More']

const format = (title: string, keywords: string[], block: BlockKind): SlashItem => ({ title, keywords, section: 'Basic', action: { kind: 'format', block } })
const insert = (title: string, keywords: string[], section: SlashMenuSection, build: (id: string) => Block): SlashItem =>
  ({ title, keywords, section, action: { kind: 'insert', build } })
const question = (subtype: 'sa' | 'sc' | 'mc' | 'la' | 'sr') => (id: string): Block => {
  const base = { id, type: 'question' as const, qid: ulid(), fractionalIndex: '', question: [] }
  if (subtype === 'sa') return { ...base, subtype, answer: [''] }
  if (subtype === 'sc') return { ...base, subtype, opts: [[], []], answer: 0 }
  if (subtype === 'mc') return { ...base, subtype, opts: [[], [], []], answer: [0, 1] }
  if (subtype === 'la') return { ...base, subtype, criteria: [], explanation: [] }
  return { ...base, subtype, answer: { src: '', transcript: '' } }
}
const grid = <T,>(count: number, make: () => T): T[] => Array.from({ length: count }, make)

/** The built-in items in menu order (Lexical `slash-menu-plugin.tsx`). */
export const slashItems: readonly SlashItem[] = [
  format('Paragraph', ['p', 'text'], 'paragraph'),
  format('Heading 1', ['h1', 'header', 'title'], 'h1'),
  format('Heading 2', ['h2', 'subheading'], 'h2'),
  format('Heading 3', ['h3'], 'h3'),
  format('Bullet List', ['ul', 'unordered'], 'bullet'),
  format('Numbered List', ['ol', 'ordered'], 'number'),
  format('Check List', ['todo', 'checkbox'], 'check'),
  format('Quote', ['quote', 'blockquote'], 'quote'),
  format('Code Block', ['pre', 'code'], 'code'),
  insert('Page', ['page', 'subpage'], 'Basic', (id) => ({ id, type: 'page', pageId: '' })),
  insert('Cloze', ['cloze', 'blank', 'question'], 'Study', (id) => ({ id, type: 'p', children: [{ id: '', type: 'cloze', qid: ulid(), fractionalIndex: '', children: [] }] })),
  insert('Short Answer', ['short answer', 'sa', 'question'], 'Study', question('sa')),
  insert('Single Choice', ['single choice', 'sc', 'question'], 'Study', question('sc')),
  insert('Multiple Choice', ['multiple choice', 'mc', 'question'], 'Study', question('mc')),
  insert('Long Answer', ['long answer', 'la', 'question'], 'Study', question('la')),
  insert('Spoken Response', ['spoken', 'response', 'audio', 'question', 'sr'], 'Study', question('sr')),
  insert('Callout', ['alert', 'notice', 'info'], 'Layout', (id) => ({ id, type: 'alert', icon: '💡', color: 'info', children: [{ id: '', type: 'p', children: [] }] })),
  insert('Toggle', ['collapse', 'details', 'disclosure'], 'Layout', (id) => ({ id, type: 'list', subtype: 'toggle', children: [{ id: '', type: 'li', children: [{ id: '', type: 'p', children: [] }] }] })),
  insert('Table', ['grid', 'rows', 'cells'], 'Layout', (id) => ({ id, type: 'table',
    children: grid(2, () => ({ id: '', type: 'tr' as const, children: grid(2, () => ({ id: '', type: 'td' as const, children: [] })) })) })),
  insert('Columns', ['cols', 'layout', 'side by side'], 'Layout', (id) => ({ id, type: 'cols',
    children: grid(2, () => ({ id: '', type: 'col' as const, width: 50, children: [{ id: '', type: 'p' as const, children: [] }] })) })),
  insert('Divider', ['hr', 'rule', 'separator'], 'Layout', (id) => ({ id, type: 'hr' })),
  insert('Embed', ['youtube', 'video', 'link', 'preview'], 'Media', (id) => ({ id, type: 'preview', subtype: 'youtube', src: '' })),
  { title: 'Image', keywords: ['photo', 'picture', 'upload'], section: 'Media', action: { kind: 'files', accept: 'image' } },
  { title: 'File', keywords: ['attachment', 'document', 'upload'], section: 'Media', action: { kind: 'files', accept: 'file' } },
  { title: 'Video', keywords: ['movie', 'clip', 'upload'], section: 'Media', action: { kind: 'files', accept: 'video' } },
  insert('Formula', ['math', 'latex', 'equation'], 'Media', (id) => ({ id, type: 'pre', latex: true, children: [{ id: '', type: 'chars', text: [] }] })),
  { title: 'Link', keywords: ['url', 'href', 'website'], section: 'Media', action: { kind: 'link' } },
  { title: 'Duplicate', keywords: ['duplicate', 'copy'], section: 'More', action: { kind: 'duplicate' } },
  // Notion "slash turn" commands (keyboard-shortcuts, using-slash-commands help: `/turnbullet`), listed only for a `turn` query.
  ...([['Text', ['text', 'paragraph'], 'paragraph'], ['Heading 1', ['h1', 'heading1'], 'h1'], ['Heading 2', ['h2', 'heading2'], 'h2'],
    ['Heading 3', ['h3', 'heading3'], 'h3'], ['Bulleted list', ['bullet', 'bulleted'], 'bullet'],
    ['Numbered list', ['number', 'numbered', 'num'], 'number'], ['To-do list', ['todo', 'checkbox'], 'check'],
    ['Quote', ['quote'], 'quote'], ['Code', ['code'], 'code'], ['Callout', ['callout'], 'callout']] as const).map(([name, words, block]): SlashItem => ({
    title: `Turn into ${name}`, keywords: words.map(word => `turn${word}`), section: 'Turn into', action: { kind: 'turn', block } })),
]

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
/** Lexical `useBasicTypeaheadTriggerMatch('/', { allowWhitespace: true, minLength: 0 })`. */
const TRIGGER = new RegExp(`(^|\\s|\\()([/]((?:[^/${PUNCTUATION}]){0,75}))$`)

/**
 * The trigger ending `text` (the row text before the caret): a `/` at a line start, after whitespace or "(", else inline
 * after any character but ":" or "/"; `start` is the slash's offset, `query` what follows it.
 */
export function slashTrigger(text: string): { start: number; query: string } | null {
  const match = TRIGGER.exec(text)
  if (match) return { start: match.index + match[1].length, query: match[3] }
  const slash = text.lastIndexOf('/')
  if (slash <= 0 || /[:/]/u.test(text[slash - 1])) return null
  const inline = TRIGGER.exec(` ${text.slice(slash)}`)
  return inline ? { start: slash, query: inline[3] } : null
}

/** The open menu's trigger at the collapsed caret of `state`, with the caret's row. */
export function slashMatch(state: Pick<CoreState, 'blocks' | 'selection'>): { row: Row; start: number; end: number; query: string } | null {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return null
  const { anchor, focus } = selection
  if (anchor.blockId !== focus.blockId || anchor.offset !== focus.offset) return null
  const entries = rows(state.blocks)
  const at = locate(entries, focus)
  const row = entries[at.row]
  if (!row) return null
  const trigger = slashTrigger(plain(row.block).slice(0, at.offset))
  return trigger && { row, start: trigger.start, end: at.offset, query: trigger.query }
}

/** The inline content a built-in item inserts into an inline-only slot (none for the link editor), or null when it
 * inserts or makes blocks. */
function inlineInsert(action: SlashAction): Blocks | null {
  if (action.kind === 'link') return []
  if (action.kind !== 'insert') return null
  const built = action.build('')
  const content = built.type === 'p' ? children(built) : [built]
  return content.every(block => isInlineContent(block)) ? content : null
}

/** The inline content a host item's block is, or null when it is block content (or the item builds no block). */
function hostInline(block: Block | undefined): Blocks | null {
  if (!block) return null
  const content = block.type === 'p' ? children(block) : [block]
  return content.every(item => isInlineContent(item)) ? content : null
}

/** Items matching `query` (every item for none): the menu's sections in order, then fuzzy score, then menu order. */
export function filterSlashItems<T extends Pick<SlashItem, 'title' | 'keywords' | 'section'>>(items: readonly T[], query: string | null): T[] {
  const turn = normalize(query ?? '').startsWith('turn')
  const listed = items.filter(item => (item.section === 'Turn into') === turn)
  if (!query) return listed
  return listed
    .map((item, index) => ({ item, index, score: fuzzyScore(item, query) }))
    .filter((candidate): candidate is typeof candidate & { score: number } => candidate.score !== null)
    .toSorted((left, right) => slashSections.indexOf(left.item.section) - slashSections.indexOf(right.item.section) ||
      right.score - left.score || left.index - right.index)
    .map(({ item }) => item)
}

/** `items` by section in menu order, each with its index in `items`. */
export function groupSlashItems<T extends Pick<SlashItem, 'section'>>(items: readonly T[]): { section: SlashMenuSection; items: { index: number; item: T }[] }[] {
  return slashSections.flatMap((section) => {
    const grouped = items.flatMap((item, index) => (item.section === section ? [{ index, item }] : []))
    return grouped.length > 0 ? [{ section, items: grouped }] : []
  })
}

function fuzzyScore(item: Pick<SlashItem, 'title' | 'keywords'>, query: string): number | null {
  const terms = normalize(query).split(' ').filter(Boolean)
  if (terms.length === 0) return 0
  const fields = [item.title, ...item.keywords].map((field) => normalize(field))
  let total = 0
  for (const term of terms) {
    let best: number | null = null
    for (const [index, field] of fields.entries()) {
      const score = fieldScore(field, term)
      if (score !== null) best = Math.max(best ?? -Infinity, score + (index === 0 ? 50 : 0))
    }
    if (best === null) return null
    total += best
  }
  return total
}

function fieldScore(field: string, query: string): number | null {
  if (field === query) return 1_000
  if (field.startsWith(query)) return 800 - (field.length - query.length)
  const substring = field.indexOf(query)
  if (substring >= 0) return 600 - substring
  let previous = -1
  let first = -1
  for (const character of query) {
    const index = field.indexOf(character, previous + 1)
    if (index < 0) return null
    if (first < 0) first = index
    previous = index
  }
  return 300 - (previous - first)
}

function normalize(value: string): string {
  return value.normalize('NFKD').toLowerCase().replaceAll(/[^a-z0-9]+/g, ' ').trim()
}

/** The empty block a format item turns an empty block into (Lexical `applyBuiltInSlashOptionToBlock`). */
function emptyBlock(kind: BlockKind): Block {
  const p = (): Block => ({ id: '', type: 'p', children: [] })
  if (kind === 'paragraph') return p()
  if (kind === 'quote') return { id: '', type: 'quote', children: [p()] }
  if (kind === 'code') return { id: '', type: 'pre', children: [] }
  if (kind === 'h1' || kind === 'h2' || kind === 'h3') return { id: '', type: 'h', level: Number(kind.slice(1)) as 1 | 2 | 3, children: [] }
  const subtype = kind === 'number' ? 'ordered' : kind === 'check' ? 'todo' : undefined
  return { id: '', type: 'list', ...(subtype ? { subtype } : {}), children: [{ id: '', type: 'li', ...(kind === 'check' ? { done: false } : {}), children: [p()] }] }
}

/** Inline content a host item builds (a page link, a pages group) lands in a paragraph, as Lexical wraps an inline node inserted at the root. */
const inlineContent = (block: Block): boolean => block.type === 'pages' || (isInline(block) && block.type !== 'page')

/**
 * Chooses the built-in item `title` for the trigger before the collapsed caret (Lexical `SlashMenuPlugin`
 * `onSelectOption`): the trigger text goes, and the item's block splits the caret's row inside the row's own container
 * (placement.ts), an empty row replaced. An inline-only slot takes an item's inline content and refuses block items. A
 * host item's `block` inserts like a built-in insert item; a media, link or host item without a block only removes the
 * trigger. The caret lands in an inserted question's prompt.
 */
export function applySlashItem(state: CoreState, title: string, block?: Block | null): void {
  const action: SlashAction | undefined = block ? { kind: 'insert', build: (id) => (inlineContent(block) ? { id, type: 'p', children: [block] } : { ...block, id }) }
    : block === null ? undefined : slashItems.find((candidate) => candidate.title === title)?.action
  if (block === undefined && !action) throw new Error(`No slash item ${title}`)
  const match = slashMatch(state)
  if (!match) throw new Error('applySlashItem needs a slash trigger before a collapsed caret')
  const { row, start, end } = match
  deleteRange(state, row.block.children, start, end)
  const collapse = (point: CorePoint) => { state.selection = { anchor: point, focus: point } }
  // A file picker, the link editor or a host action opens in the view, at the caret where the trigger was.
  if (!action || action.kind === 'files' || action.kind === 'link') {
    collapse(pointAt(row, start))
    return
  }
  // An inline-only slot (a table cell, a question field) takes an item's inline content at the caret, and refuses
  // block items (INV-LEVEL); Lexical `$insertTable` likewise refuses nested tables.
  if (inInlineSlot(state, row)) {
    const content = block ? hostInline(block) : inlineInsert(action)
    if (content && content.length > 0) {
      const tail = takeInlineTail(state, row.block.children, start)
      spliceBlocks(state, row.block.children, row.block.children.length, 0, ...content, ...tail)
    }
    collapse(pointAt(row, start + (content ?? []).reduce((sum, item) => sum + plain(item).length, 0)))
    return
  }
  // Actions operate on the complete block after removing only the command, without insertion's row splitting.
  if (action.kind === 'turn') {
    formatBlock(state, rows(state.blocks).find(entry => entry.block.id === row.block.id) ?? row, action.block)
    // The converted row holds the caret (formatBlock puts it at the row's end); the caret stays where the command was.
    const entries = state.selection && rows(state.blocks)
    const moved = entries && state.selection && entries[locate(entries, state.selection.focus).row]
    if (moved) collapse(pointAt(moved, start))
    return
  }
  if (action.kind === 'duplicate') {
    duplicateCaretBlock(state, row, start)
    return
  }
  const built = action.kind === 'insert' ? action.build('') : emptyBlock(action.block)
  // Paragraph on an empty paragraph keeps it; every other choice lands through the ownership contract (placement.ts):
  // the row splits at the caret inside its own container and the item's block stands between the halves, an empty row
  // replaced.
  if (action.kind === 'format' && built.type === 'p' && row.block.type === 'p' && row.block.children.length === 0) {
    collapse(pointAt(row, 0))
    return
  }
  // Lexical `replaceTopLevelBlock`: an inserted question takes the caret in its own prompt field.
  const ownsCaret = built.type === 'question'
  const { home, index } = insertAt(state, row, start, built, !ownsCaret)
  if (action.kind === 'format') {
    const last = rows([built]).at(-1)
    if (last) collapse(pointAt(last, plain(last.block).length))
    return
  }
  // Lexical `$insertTable`: the caret lands in the first cell (`tableNode.getFirstDescendant().select()`).
  const firstCell = built.type === 'table' ? rows([built])[0] : undefined
  if (firstCell) { collapse(pointAt(firstCell, 0)); return }
  // Notion: a new toggle or callout takes the caret in its own first line (writing-and-editing-basics, `/callout`).
  const inside = ['list', 'container'].includes(blockClass(built)) ? rows([findBlock(state.blocks, built.id) ?? built])[0] : undefined
  if (inside) { collapse(pointAt(inside, 0)); return }
  collapse(after(home, index))
  if (ownsCaret && state.selection) state.selection.field = { blockId: built.id, role: 'prompt' }
}
